import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import HeroBanner from '@/components/feature/HeroBanner';
import MovieCard from '@/components/feature/MovieCard';
import { films, categories } from '@/mocks/films';
import type { Film } from '@/mocks/films';

export default function Home() {
  const [activeCategory, setActiveCategory] = useState('all');

  const filteredFilms = useMemo(() => {
    if (activeCategory === 'all') return films;
    return films.filter((f: Film) => f.category === activeCategory);
  }, [activeCategory]);

  const trendingFilms = useMemo(() => films.filter((f: Film) => f.trending).slice(0, 8), []);
  const newestFilms = useMemo(() => films.filter((f: Film) => f.newRelease).slice(0, 8), []);
  const dramaFilms = useMemo(() => films.filter((f: Film) => f.category === 'drama').slice(0, 8), []);
  const movieFilms = useMemo(() => films.filter((f: Film) => f.category === 'movie').slice(0, 8), []);
  const animeFilms = useMemo(() => films.filter((f: Film) => f.category === 'anime').slice(0, 8), []);

  const categoryMap: Record<string, string> = {
    drama: '电视剧',
    movie: '电影',
    variety: '综艺',
    anime: '动漫',
  };

  const homepageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '木瓜影视',
    url: 'https://www.muguays.com',
    description: '木瓜影视在线观看平台，免费在线直接看最新电视剧电影，网页看视频高清不缓冲，即点即播无需下载注册。',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://www.muguays.com/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
    inLanguage: 'zh-CN',
  };

  return (
    <>
      <SEOHead
        title="木瓜影视 - 在线观看：免费在线直接看最新电视剧电影全集，网页看视频高清不缓冲即点即播无需下载"
        description="木瓜影视在线观看平台，免费在线直接看最新电视剧电影，网页看视频高清不缓冲，即点即播无需下载注册，方便快捷。涵盖最新电视剧、最新电影、热门综艺、动漫番剧。"
        keywords="在线观看,免费在线,木瓜影视,直接看,网页看视频,即点即播,高清不缓冲,无需下载,最新电视剧,最新电影,免费看,在线播放"
        canonicalPath="/"
        schema={homepageSchema}
      />

      <HeroBanner />

      <section className="max-w-[1400px] mx-auto px-4 md:px-6 py-10 md:py-14">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">热门推荐</h2>
          <Link
            to="/category/all"
            className="text-sm text-foreground-600 hover:text-primary-500 transition-colors flex items-center gap-1 cursor-pointer whitespace-nowrap"
          >
            查看全部
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-line text-xs"></i>
            </div>
          </Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-5">
          {trendingFilms.map((film: Film) => (
            <MovieCard key={film.id} film={film} />
          ))}
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-4 md:px-6 py-10 md:py-14 border-t border-background-200">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">最新上线</h2>
          <Link
            to="/category/all"
            className="text-sm text-foreground-600 hover:text-primary-500 transition-colors flex items-center gap-1 cursor-pointer whitespace-nowrap"
          >
            查看全部
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-line text-xs"></i>
            </div>
          </Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-5">
          {newestFilms.map((film: Film) => (
            <MovieCard key={film.id} film={film} />
          ))}
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-4 md:px-6 py-10 md:py-14 border-t border-background-200">
        <div className="mb-6">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-1">分类浏览</h2>
          <p className="text-foreground-500 text-sm">按分类快速找到你想看的影视内容</p>
        </div>

        <nav aria-label="分类导航" className="flex items-center gap-2 mb-8 flex-wrap">
          <div className="flex items-center bg-background-100 rounded-full px-1 py-1">
            {categories.map((cat) => (
              <button
                key={cat.slug}
                onClick={() => setActiveCategory(cat.slug)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  activeCategory === cat.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'text-foreground-600 hover:text-foreground-900'
                }`}
              >
                {cat.name}
              </button>
            ))}
          </div>
        </nav>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-5">
          {filteredFilms.map((film: Film) => (
            <MovieCard key={film.id} film={film} />
          ))}
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-4 md:px-6 py-10 md:py-14 border-t border-background-200">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">热门电视剧</h2>
          <Link
            to="/category/drama"
            className="text-sm text-foreground-600 hover:text-primary-500 transition-colors flex items-center gap-1 cursor-pointer whitespace-nowrap"
          >
            更多电视剧
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-line text-xs"></i>
            </div>
          </Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-5">
          {dramaFilms.map((film: Film) => (
            <MovieCard key={film.id} film={film} />
          ))}
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-4 md:px-6 py-10 md:py-14 border-t border-background-200">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">热门电影</h2>
          <Link
            to="/category/movie"
            className="text-sm text-foreground-600 hover:text-primary-500 transition-colors flex items-center gap-1 cursor-pointer whitespace-nowrap"
          >
            更多电影
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-line text-xs"></i>
            </div>
          </Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-5">
          {movieFilms.map((film: Film) => (
            <MovieCard key={film.id} film={film} />
          ))}
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-4 md:px-6 py-10 md:py-14 border-t border-background-200">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">热门动漫</h2>
          <Link
            to="/category/anime"
            className="text-sm text-foreground-600 hover:text-primary-500 transition-colors flex items-center gap-1 cursor-pointer whitespace-nowrap"
          >
            更多动漫
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-line text-xs"></i>
            </div>
          </Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-5">
          {animeFilms.map((film: Film) => (
            <MovieCard key={film.id} film={film} />
          ))}
        </div>
      </section>

      <section className="bg-background-100 py-14 md:py-18">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 text-center">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-3">为什么选择木瓜影视？</h2>
          <p className="text-foreground-500 text-sm mb-10 max-w-2xl mx-auto">
            我们致力于为用户提供最优质的在线观影体验
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { icon: 'ri-flashlight-line', title: '即点即播', desc: '点击即可观看，零等待' },
              { icon: 'ri-hd-line', title: '高清画质', desc: '1080P超清视觉享受' },
              { icon: 'ri-download-cloud-2-line', title: '无需下载', desc: '网页直接播放，不占空间' },
              { icon: 'ri-user-forbid-line', title: '无需注册', desc: '打开就看，简单自由' },
            ].map((item) => (
              <div key={item.title} className="flex flex-col items-center">
                <div className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center rounded-2xl bg-background-200 mb-4">
                  <i className={`${item.icon} text-2xl md:text-3xl text-primary-500`}></i>
                </div>
                <h3 className="text-foreground-900 font-semibold text-sm md:text-base mb-1">{item.title}</h3>
                <p className="text-foreground-500 text-xs">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}